import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { errText } from '../api';
import type { Action } from '../types';

// ---------- formatting ----------

export const ACTION_LABEL: Record<Action, string> = {
  allow: 'Allow',
  flag: 'Flag',
  block_message: 'Block message',
  block_message_and_user: 'Block msg + user',
};

export const humanize = (s: string) => s.replace(/_/g, ' ');

export function fmtDate(iso: string | null | undefined, withSeconds = false) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {}),
    ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
  });
}

export function fmtRelative(iso: string | null | undefined) {
  if (!iso) return 'never';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return fmtDate(iso);
}

export const fmtNum = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString());
export const pct = (n: number | null | undefined) => (n == null ? '—' : `${Math.round(n * 100)}%`);

// ---------- badges ----------

export function ActionBadge({ action }: { action: Action | null | undefined }) {
  if (!action) return <span className="badge badge-muted">pending</span>;
  return <span className={`badge badge-${action}`}>{ACTION_LABEL[action]}</span>;
}

const STATUS_TONE: Record<string, string> = {
  ready: 'ok',
  success: 'ok',
  done: 'ok',
  active: 'ok',
  confirmed: 'ok',
  pending: 'warn',
  queued: 'warn',
  processing: 'warn',
  failed: 'bad',
  blocked: 'bad',
  overridden: 'warn',
};

export function StatusBadge({ status, label }: { status: string | null | undefined; label?: string }) {
  if (!status) return <span className="badge badge-muted">{label ?? '—'}</span>;
  return <span className={`badge badge-${STATUS_TONE[status] ?? 'muted'}`}>{label ?? humanize(status)}</span>;
}

export function SeverityBadge({ severity }: { severity: string | null | undefined }) {
  if (!severity || severity === 'none') return <span className="muted">—</span>;
  return <span className={`sev sev-${severity}`}>{severity}</span>;
}

// ---------- small building blocks ----------

export function CopyButton({ text, label = 'Copy', className = '' }: { text: string; label?: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button type="button" className={`btn btn-sm ${className}`} onClick={copy}>
      {copied ? 'Copied' : label}
    </button>
  );
}

export function SecretBox({ value, warning }: { value: string; warning?: ReactNode }) {
  return (
    <div className="secret-box">
      {warning && <p className="secret-warning">{warning}</p>}
      <div className="secret-row">
        <code className="mono secret-value">{value}</code>
        <CopyButton text={value} />
      </div>
    </div>
  );
}

export function CodeBlock({ code, label }: { code: string; label?: string }) {
  return (
    <div className="codeblock">
      <div className="codeblock-head">
        <span>{label}</span>
        <CopyButton text={code} />
      </div>
      <pre>
        <code>{code}</code>
      </pre>
    </div>
  );
}

export function Alert({ kind = 'error', children }: { kind?: 'error' | 'info' | 'success' | 'warn'; children: ReactNode }) {
  if (!children) return null;
  return (
    <div className={`alert alert-${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="loading">
      <span className="spinner" aria-hidden /> {label}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Card({
  title,
  actions,
  children,
  className = '',
  subtitle,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <header className="card-head">
          <div>
            {title && <h2>{title}</h2>}
            {subtitle && <p className="card-sub">{subtitle}</p>}
          </div>
          {actions && <div className="card-actions">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  useEffect(() => {
    document.title = `${title} · MsgGuard`;
  }, [title]);
  return (
    <div className="page-head">
      <div>
        <h1>{title}</h1>
        {subtitle && <p className="page-sub">{subtitle}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

export function Toggle({
  checked,
  onChange,
  disabled,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label?: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <label className={`toggle ${disabled ? 'is-disabled' : ''}`}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="toggle-track" aria-hidden>
        <span className="toggle-thumb" />
      </span>
      {(label || hint) && (
        <span className="toggle-text">
          {label && <span className="toggle-label">{label}</span>}
          {hint && <span className="hint">{hint}</span>}
        </span>
      )}
    </label>
  );
}

export function ScoreBar({ label, value, threshold }: { label: string; value: number | null | undefined; threshold?: number }) {
  const v = value ?? 0;
  const tone = value == null ? 'muted' : v >= 0.8 ? 'bad' : v >= 0.5 ? 'warn' : 'ok';
  return (
    <div className="scorebar">
      <div className="scorebar-head">
        <span>{label}</span>
        <span className="mono">{value == null ? 'n/a' : v.toFixed(2)}</span>
      </div>
      <div className="scorebar-track">
        <div className={`scorebar-fill tone-${tone}`} style={{ width: `${Math.round(v * 100)}%` }} />
        {threshold != null && <div className="scorebar-mark" style={{ left: `${threshold * 100}%` }} title={`Flag threshold ${threshold}`} />}
      </div>
    </div>
  );
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${wide ? 'modal-wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <header className="modal-head">
          <h2>{title}</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

// ---------- hooks ----------

/** Loads data with an async function; reloads whenever `deps` change. */
export function useLoader<T>(fn: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const seq = useRef(0);

  const reload = useCallback(async (silent = false) => {
    const id = ++seq.current;
    if (!silent) setLoading(true);
    try {
      const result = await fnRef.current();
      if (id === seq.current) {
        setData(result);
        setError(null);
      }
    } catch (err) {
      if (id === seq.current) setError(errText(err));
    } finally {
      if (id === seq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, setData, error, loading, reload };
}

/** Wraps a mutating action with busy/error state. */
export function useAction() {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(async <T,>(key: string, fn: () => Promise<T>): Promise<T | undefined> => {
    setBusy(key);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      setError(errText(err));
      return undefined;
    } finally {
      setBusy(null);
    }
  }, []);
  return { busy, error, setError, run };
}
